import { useState, useEffect, useRef, useCallback } from 'react';
import {
  startVideoStream,
  stopVideoStream,
  generateStreamUrl,
  getAvailableChannels
} from '../services/videoPlayerService';
import { getDeviceChannelInfo, generateChannelList } from '../services/deviceChannelsService';

interface VideoPlayerStats {
  totalBytes?: number;
}

interface UseVideoPlayerParams {
  imei: string;
  brandId?: number;
  deviceModelId?: number;
}

export const useVideoPlayer = ({ imei, brandId, deviceModelId }: UseVideoPlayerParams) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [currentChannel, setCurrentChannel] = useState<number | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [availableChannels, setAvailableChannels] = useState<number[]>([]);
  const [stats, setStats] = useState<VideoPlayerStats>({
    totalBytes: 0
  });
  const [totalBytesLoaded, setTotalBytesLoaded] = useState<number>(0);
  const lastRequestTimeRef = useRef<number>(0);
  const lastStopTimeRef = useRef<number>(0);
  const debounceTimeMs = 1000; // Tiempo mínimo entre solicitudes (1 segundo)

  // Cargar canales disponibles basados en el modelo del dispositivo
  useEffect(() => {
    const loadChannels = async () => {
      if (!imei) {
        // Si no hay IMEI, usar canales por defecto
        console.log('No IMEI provided, using default channels');
        setAvailableChannels([1, 2, 3, 4, 5]);
        return;
      }

      console.log('Loading channels for IMEI:', imei);
      
      try {
        // Primero intentar obtener la información del modelo del dispositivo
        const deviceInfo = await getDeviceChannelInfo(imei);
        console.log('Device info received:', deviceInfo);
        
        if (deviceInfo && deviceInfo.videoStreamingChannels > 0) {
          // Usar la cantidad de canales del modelo del dispositivo
          const channels = generateChannelList(deviceInfo.videoStreamingChannels);
          console.log('Setting channels from device info:', channels);
          setAvailableChannels(channels);
          return;
        } else if (deviceInfo && deviceInfo.videoStreamingChannels === 0) {
          // Dispositivo sin canales de video (no es compatible)
          console.log('Device has no video channels');
          setAvailableChannels([]);
          return;
        }
        
        // Si no se pudo obtener la información del dispositivo, fallback al método original
        console.log('Device info not available, trying fallback method');
        const channels = await getAvailableChannels(imei);
        
        if (channels && channels.length > 0) {
          console.log('Setting channels from fallback method:', channels);
          setAvailableChannels(channels);
        } else {
          // Si el fallback tampoco devuelve canales, usar canales por defecto
          console.log('Fallback method returned no channels, using default channels');
          setAvailableChannels([1, 2, 3, 4, 5]);
        }
      } catch (err) {
        console.error('Error loading available channels:', err);
        // Fallback a canales por defecto en caso de error
        console.log('Using default channels due to error');
        setAvailableChannels([1, 2, 3, 4, 5]);
      }
    };

    loadChannels();
  }, [imei]);

  // Función para iniciar stream
  const startStream = useCallback(
    async (channel: number): Promise<boolean> => {
      if (!imei) {
        setError('No device IMEI specified');
        return false;
      }

      // Protección contra múltiples clicks rápidos, pero permitir inicio inmediato después de stop
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTimeRef.current;
      const timeSinceLastStop = now - lastStopTimeRef.current;
      
      // Permitir inicio inmediato si acaba de parar (dentro de 2 segundos), sino aplicar debounce normal
      if (timeSinceLastRequest < debounceTimeMs && timeSinceLastStop > 2000) {
        console.log('Request throttled. Please wait before trying again.');
        return false;
      }
      lastRequestTimeRef.current = now;

      setIsLoading(true);
      setError(null);

      try {
        console.log(`Starting stream for IMEI: ${imei}, Channel: ${channel}`);
        const success = await startVideoStream({ imei, channel, brandId, deviceModelId });

        if (success.success) {
          const url = success.streamUrl || generateStreamUrl(channel, imei);
          console.log(`Stream URL: ${url}`);
          setStreamUrl(url);
          setCurrentChannel(channel);
          setIsStreaming(true);
          return true;
        } else {
          setError(success.message || 'Failed to start streaming');
          return false;
        }
      } catch (err) {
        setError(`Error starting stream: ${err instanceof Error ? err.message : 'Unknown'}`);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [imei, debounceTimeMs]
  );

  // Función para detener stream
  const stopStream = useCallback(async (): Promise<boolean> => {
    if (!currentChannel || !imei) return false;

    // Protección contra múltiples clicks rápidos
    const now = Date.now();
    if (now - lastRequestTimeRef.current < debounceTimeMs) {
      console.log('Stop request throttled. Please wait before trying again.');
      return false;
    }
    lastRequestTimeRef.current = now;
    lastStopTimeRef.current = now; // Registrar cuándo se detuvo para permitir inicio inmediato

    setIsLoading(true);
    setError(null);

    try {
      console.log(`Stopping stream for IMEI: ${imei}, Channel: ${currentChannel}`);
      const success = await stopVideoStream({ imei, channel: currentChannel, brandId, deviceModelId });

      if (success.success) {
        setIsStreaming(false);
        setStreamUrl(null);
        setCurrentChannel(null);
        console.log('Stream stopped successfully');
        return true;
      } else {
        setError(success.message || 'Failed to stop streaming');
        return false;
      }
    } catch (err) {
      setError(`Error stopping stream: ${err instanceof Error ? err.message : 'Unknown'}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentChannel, imei, debounceTimeMs]);

  // Función específica para cambio de canal (bypasea el throttling)
  const switchChannel = useCallback(
    async (newChannel: number): Promise<boolean> => {
      if (!imei) {
        setError('No device IMEI specified');
        return false;
      }

      // Si ya está en el mismo canal, no hacer nada
      if (currentChannel === newChannel && isStreaming) {
        return true;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Paso 1: Detener stream actual si existe
        if (isStreaming && currentChannel) {
          console.log(`Switching channel: Stopping current channel ${currentChannel}`);
          const stopSuccess = await stopVideoStream({ imei, channel: currentChannel, brandId, deviceModelId });
          if (!stopSuccess.success) {
            console.warn('Failed to stop current stream, continuing anyway');
          }

          // Limpiar estado inmediatamente
          setIsStreaming(false);
          setStreamUrl(null);
          setCurrentChannel(null);

          // Pequeña pausa para asegurar que el stop se complete
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Paso 2: Iniciar nuevo stream
        console.log(`Switching channel: Starting new channel ${newChannel}`);
        const success = await startVideoStream({ imei, channel: newChannel, brandId, deviceModelId });

        if (success.success) {
          const url = success.streamUrl || generateStreamUrl(newChannel, imei);
          console.log(`Channel switch successful: Stream URL ${url}`);
          setStreamUrl(url);
          setCurrentChannel(newChannel);
          setIsStreaming(true);
          
          // Actualizar las referencias de tiempo para evitar conflictos futuros
          const now = Date.now();
          lastRequestTimeRef.current = now;
          lastStopTimeRef.current = now;
          
          return true;
        } else {
          setError(success.message || 'Failed to start new channel');
          return false;
        }
      } catch (err) {
        const errorMsg = `Error switching channel: ${err instanceof Error ? err.message : 'Unknown'}`;
        setError(errorMsg);
        console.error(errorMsg);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [imei, currentChannel, isStreaming]
  );

  const updateStats = useCallback((newStats: Partial<VideoPlayerStats>) => {
    setStats((prev) => ({ ...prev, ...newStats }));
  }, []);

  const updateBytesLoaded = useCallback((bytes: number) => {
    setTotalBytesLoaded((prev) => prev + bytes);
  }, []);

  // Limpiar recursos cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (isStreaming && currentChannel && imei) {
        stopVideoStream({ imei, channel: currentChannel, brandId, deviceModelId }).catch(console.error);
      }
    };
  }, [isStreaming, currentChannel, imei, brandId, deviceModelId]);

  return {
    isLoading,
    error,
    streamUrl,
    currentChannel,
    isStreaming,
    stats,
    totalBytesLoaded,
    availableChannels,
    startStream,
    stopStream,
    switchChannel,
    updateStats,
    updateBytesLoaded,
    setIsStreaming,
    setStreamUrl,
    setCurrentChannel
  };
};
